import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelection } from '../contexts/selectionContext';
import { setSyncTimeout } from '../helpers/setSyncTimeout';
import { createSelectionOverlay, removeSelectionOverlay } from '../helpers/selectionOverlayUtils';

export function useTextSelection({ fieldId, disabled = false }) {
    const containerRef = useRef(null);
    const overlayRef = useRef(null);

    const [selection, setSelection] = useState({
        text: '',
        isActive: false,
        range: null,
        position: { top: 0, left: 0 },
        fieldId: fieldId,
    });
    const { registerField } = useSelection();

    const [feedbackModal, setFeedbackModal] = useState({
        isOpen: false,
        quotedText: null,
        range: null,
        fieldId: null,
    });

    const handleSelectionFromContext = useCallback(
        (newSelectionData) => {
            if (disabled) return;

            const newSelection = {
                ...newSelectionData,
                fieldId,
            };

            setSelection((prevSelection) => {
                const nothingChanged =
                    JSON.stringify(prevSelection) === JSON.stringify(newSelection);
                if (nothingChanged) return prevSelection;

                return newSelection;
            });
        },
        [disabled, fieldId]
    );

    useEffect(() => {
        if (disabled || !fieldId) return;

        return registerField(fieldId, containerRef, handleSelectionFromContext);
    }, [disabled, fieldId, registerField, handleSelectionFromContext]);

    useEffect(() => {
        if (!feedbackModal.isOpen || !feedbackModal.range) {
            setSyncTimeout(() => clearSelectionOverlay, 100);
        }
    }, [feedbackModal.isOpen, feedbackModal.range]);

    useEffect(() => {
        return () => {
            setSelection({
                text: '',
                isActive: false,
                range: null,
                position: { top: 0, left: 0 },
                fieldId: fieldId,
            });
            clearSelectionOverlay();
        };
    }, []);

    const clearSelection = () => {
        window.getSelection().removeAllRanges();
        clearSelectionOverlay();
    };

    const restoreSelection = (range) => {
        if (!range) return;

        const domSelection = window.getSelection();
        domSelection.removeAllRanges();
        domSelection.addRange(range);
    };

    const openFeedbackWithQuote = useCallback(() => {
        const range = selection.range;

        setFeedbackModal({
            isOpen: true,
            quotedText: selection.text,
            range,
            fieldId,
        });

        window.getSelection().removeAllRanges();
        if (range) {
            overlayRef.current = createSelectionOverlay(range, containerRef, fieldId);
        }
    }, [selection]);

    const clearSelectionOverlay = useCallback(() => {
        if (!overlayRef.current) return;

        removeSelectionOverlay(fieldId);
        overlayRef.current = null;
    }, [fieldId]);

    const closeFeedbackModal = useCallback(
        ({ keepSelection = true } = {}) => {
            const range = feedbackModal.range;

            setFeedbackModal({ isOpen: false, quotedText: null, range: null, fieldId: null });

            if (keepSelection && range) {
                setSyncTimeout(() => {
                    clearSelectionOverlay();
                    restoreSelection(range);
                }, 50);
            } else {
                clearSelectionOverlay();
            }
        },
        [feedbackModal]
    );

    return {
        containerRef,
        selection,
        clearSelection,
        openFeedbackWithQuote,
        closeFeedbackModal,
        feedbackModal,
    };
}
